import React, { useEffect, useState, useCallback } from "react";
import { Plus, Megaphone, Sparkles, ImagePlus, Instagram, Trash2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PageHeader } from "@/components/Layout";
import { Chip, EmptyState } from "@/components/Bits";
import AuthImage from "@/components/AuthImage";
import { PLATFORMS } from "@/lib/ui";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full bg-[#050505] border border-white/10 text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-white focus:ring-1 focus:ring-white transition-colors";

const COLUMNS = ["Draft", "Scheduled", "Published"];
const SOCIAL_PLATFORMS = ["Instagram", "Facebook", "LinkedIn", "Twitter/X", "YouTube"];

function statusChip(s) {
  if (s === "Published") return "bg-[#00FF66]/10 border-[#00FF66]/30 text-[#00FF66]";
  if (s === "Scheduled") return "bg-[#FFD600]/10 border-[#FFD600]/30 text-[#FFD600]";
  return "bg-white/5 border-white/15 text-zinc-400";
}

const EMPTY = { brand: "", platform: "Instagram", topic: "", caption: "", image_path: "", data_url: "", scheduled_date: "", status: "Draft" };

export default function Social() {
  const [posts, setPosts] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [genCap, setGenCap] = useState(false);
  const [genImg, setGenImg] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/social/posts").then((r) => setPosts(r.data)), []);
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data));
  }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const generateCaption = async () => {
    if (!form.brand) return toast.error("Pick a brand first");
    setGenCap(true);
    try {
      const { data } = await api.post("/social/generate-caption", { brand: form.brand, topic: form.topic, platform: form.platform });
      set("caption", data.caption);
      toast.success("Caption generated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setGenCap(false); }
  };

  const generateImage = async () => {
    const prompt = form.topic || form.caption || `${form.platform} marketing post for ${form.brand}`;
    if (!form.brand) return toast.error("Pick a brand first");
    setGenImg(true);
    try {
      const { data } = await api.post("/social/generate-image", { prompt: `${prompt}. High quality ${form.platform} social media image for the brand ${form.brand}.` });
      setForm((f) => ({ ...f, image_path: data.image_path, data_url: data.data_url }));
      toast.success("Image generated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setGenImg(false); }
  };

  const save = async () => {
    if (!form.brand || !form.caption) return toast.error("Brand and caption are required");
    setSaving(true);
    try {
      await api.post("/social/posts", {
        brand: form.brand, platform: form.platform, caption: form.caption,
        image_path: form.image_path, scheduled_date: form.scheduled_date, status: form.status,
      });
      toast.success("Post scheduled");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => { await api.delete(`/social/posts/${id}`); load(); };

  return (
    <div>
      <PageHeader
        title="Social Scheduler"
        subtitle="Plan, generate & schedule content per brand"
        actions={
          <button data-testid="new-post-button" onClick={() => { setForm(EMPTY); setOpen(true); }} className="flex items-center gap-2 text-sm bg-white text-black font-medium px-4 py-2 rounded-sm hover:bg-zinc-200 transition-colors active:scale-[0.98]">
            <Plus size={16} /> New Post
          </button>
        }
      />
      <div className="p-8">
        {posts.length === 0 ? (
          <EmptyState icon={Megaphone} title="No scheduled posts" hint="Create your first social post." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {COLUMNS.map((col) => {
              const items = posts.filter((p) => p.status === col);
              return (
                <div key={col} data-testid={`social-col-${col}`} className="bg-[#0a0a0a] border border-white/5 rounded-sm p-4">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-mono text-xs uppercase tracking-wider text-zinc-400">{col}</span>
                    <span className="text-xs font-mono text-zinc-600">{items.length}</span>
                  </div>
                  <div className="space-y-4">
                    {items.map((p) => (
                      <div key={p.id} data-testid={`social-card-${p.id}`} className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden group">
                        <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10">
                          <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[10px] font-display font-bold">{p.brand.charAt(0)}</div>
                          <span className="text-xs text-zinc-200 font-medium truncate flex-1">{p.brand}</span>
                          <Instagram size={13} className="text-zinc-500" />
                        </div>
                        {p.image_path ? (
                          <AuthImage path={p.image_path} className="w-full h-40 object-cover" />
                        ) : (
                          <div className="w-full h-40 das-grid bg-[#0a0a0a] flex items-center justify-center text-zinc-700 text-xs font-mono">No image</div>
                        )}
                        <div className="p-3">
                          <p className="text-xs text-zinc-300 leading-relaxed line-clamp-3">{p.caption}</p>
                          <div className="flex items-center justify-between mt-3 pt-2 border-t border-white/10">
                            <div className="flex items-center gap-2">
                              <Chip className={statusChip(p.status)}>{p.platform}</Chip>
                              <span className="text-[10px] font-mono text-zinc-500">{p.scheduled_date || "—"}</span>
                            </div>
                            <button data-testid={`delete-post-${p.id}`} onClick={() => remove(p.id)} className="text-zinc-600 hover:text-[#FF6259] opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {items.length === 0 && <p className="text-xs text-zinc-700 font-mono text-center py-6">Empty</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">New Social Post</DialogTitle>
            <DialogDescription className="sr-only">Compose a social post with AI caption and image, then schedule it.</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
            {/* editor */}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <select data-testid="post-brand-select" className={inputCls} value={form.brand} onChange={(e) => set("brand", e.target.value)}>
                  <option value="">Select brand *</option>
                  {clients.map((c) => <option key={c.id} value={c.brand}>{c.brand}</option>)}
                </select>
                <select className={inputCls} value={form.platform} onChange={(e) => set("platform", e.target.value)}>
                  {SOCIAL_PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <input data-testid="post-topic-input" className={inputCls} placeholder="Topic / idea (used for AI)" value={form.topic} onChange={(e) => set("topic", e.target.value)} />
              <div className="flex gap-2">
                <button data-testid="gen-caption-button" onClick={generateCaption} disabled={genCap} className="flex-1 flex items-center justify-center gap-2 text-sm bg-[#007AFF]/10 border border-[#007AFF]/30 text-[#4DA3FF] hover:bg-[#007AFF]/20 py-2 rounded-sm transition-colors disabled:opacity-60">
                  {genCap ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} AI Caption
                </button>
                <button data-testid="gen-image-button" onClick={generateImage} disabled={genImg} className="flex-1 flex items-center justify-center gap-2 text-sm bg-[#AF52DE]/10 border border-[#AF52DE]/30 text-[#C77DFF] hover:bg-[#AF52DE]/20 py-2 rounded-sm transition-colors disabled:opacity-60">
                  {genImg ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />} AI Image
                </button>
              </div>
              <textarea data-testid="post-caption-input" className={inputCls} rows={5} placeholder="Caption *" value={form.caption} onChange={(e) => set("caption", e.target.value)} />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="post-date-input" type="date" className={inputCls} value={form.scheduled_date} onChange={(e) => set("scheduled_date", e.target.value)} />
                <select className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)}>
                  {COLUMNS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <button data-testid="post-submit-button" onClick={save} disabled={saving} className="w-full text-sm bg-white text-black font-medium py-2.5 rounded-sm hover:bg-zinc-200 transition-colors active:scale-[0.98] disabled:opacity-60">
                {saving ? "Saving…" : "Schedule Post"}
              </button>
            </div>

            {/* live preview */}
            <div>
              <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">Live Preview</div>
              <div className="bg-white text-black rounded-sm overflow-hidden border border-white/10 max-w-[340px]">
                <div className="flex items-center gap-2 px-3 py-2.5">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold">{(form.brand || "D").charAt(0)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold truncate">{form.brand || "your_brand"}</div>
                    <div className="text-[10px] text-zinc-500">{form.platform}</div>
                  </div>
                  <span className="text-zinc-400 text-lg leading-none">⋯</span>
                </div>
                {form.data_url ? (
                  <img src={form.data_url} alt="preview" className="w-full aspect-square object-cover" />
                ) : (
                  <div className="w-full aspect-square bg-zinc-100 flex items-center justify-center text-zinc-400 text-sm">
                    {genImg ? "Generating…" : "Image preview"}
                  </div>
                )}
                <div className="px-3 py-2">
                  <div className="flex gap-3 text-zinc-800 mb-1.5">
                    <span>♥</span><span>💬</span><span>➤</span>
                  </div>
                  <p className="text-xs leading-relaxed whitespace-pre-wrap">
                    <span className="font-semibold">{form.brand || "your_brand"}</span>{" "}
                    {form.caption || "Your caption will appear here…"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
